import React, { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQueryClient } from 'react-query'

import { LoaderMain } from 'components/loader'
import { useNotificationBar } from 'context/notificationContext'
import { getErrorResponse } from 'services/util'
import { QueryKeys } from 'hooks/_query_keys'
import { useAuth } from 'context/authContext'
import { hasArticlePermission } from 'constants/permission.utils'
import {
    Article,
    ArticlePayload,
    connectedContentFromForm,
    seoFieldsFromForm,
    unwrapResponse,
    useGetArticleDetails,
    usePublishArticle,
    useReviewArticle,
    useSubmitArticle,
    useUpdateArticle,
} from 'hooks/article'
import ArticleForm, { ArticleFormHelpers, SubmitIntent } from './Articleform'
import { ArticleFormValues } from './validation'

const EditArticle: React.FC = () => {
    const { id } = useParams<{ id: string }>()
    const navigate = useNavigate()
    const notification = useNotificationBar()
    const queryClient = useQueryClient()

    const { data, isLoading, isError, refetch } = useGetArticleDetails(id)
    const article = unwrapResponse(data) as Article | undefined

    const auth = useAuth()
    const permissions = auth?.user?.permissions
    const canUpdate = hasArticlePermission(permissions, 'update')
    const canReviewArticle = hasArticlePermission(permissions, 'review') && article?.status === 'in_review'
    const canPublishArticle = hasArticlePermission(permissions, 'publish') && article?.status === 'approved'
    const canSubmitArticle = canUpdate && (article?.status === 'draft' || article?.status === 'changes_requested')
    const readOnly = Boolean(article && article.status === 'in_review')

    const updateArticle = useUpdateArticle()
    const publishArticle = usePublishArticle()
    const submitArticle = useSubmitArticle()
    const reviewArticle = useReviewArticle()
    const [busy, setBusy] = useState(false)
    const [conflict, setConflict] = useState<string | null>(null)

    const onSubmit = async (
        values: ArticleFormValues,
        intent: SubmitIntent,
        { setError }: ArticleFormHelpers
    ) => {
        if (!article || !id) return

        if (intent === 'publish') {
            if (!values.seoTitle?.trim()) {
                setError('seoTitle', { message: 'SEO title is required before publishing' })
                return
            }
            if (!values.metaDescription?.trim()) {
                setError('metaDescription', { message: 'Meta description is required before publishing' })
                return
            }
        }

        const payload: ArticlePayload = {
            title: values.title.trim(),
            slug: values.slug?.trim() || article.slug,
            short_description: values.short_description.trim(),
            author: values.author.trim(),
            content_html: values.content_html || '',
            // keep the current status on "Save changes"; publishing goes via the publish route
            status: intent === 'publish' ? 'published' : article.status,
            // optimistic locking: reject if someone else saved after we loaded
            expected_version: article.version,
            ...seoFieldsFromForm(values),
            ...connectedContentFromForm(values.connectedContent),
        }

        setBusy(true)
        setConflict(null)

        try {
            if (intent === 'publish') {
                await publishArticle.mutateAsync({ id, data: payload })
            } else if (intent === 'submit') {
                const saved = unwrapResponse(await updateArticle.mutateAsync({ id, data: payload })) as Article
                await submitArticle.mutateAsync({ id, expected_version: saved?.version })
            } else {
                await updateArticle.mutateAsync({ id, data: payload })
            }

            queryClient.invalidateQueries([QueryKeys.ARTICLES])
            queryClient.invalidateQueries([QueryKeys.ARTICLE_DETAIL, id])
            notification.push({
                title: intent === 'publish' ? 'Article published' : intent === 'submit' ? 'Submitted for review' : 'Article updated',
                type: 'success',
                message:
                    intent === 'publish'
                        ? 'The article is now published.'
                        : intent === 'submit'
                        ? 'The article is waiting for review.'
                        : 'Your changes were saved.',
            })
            navigate('/articles')
        } catch (err: any) {
            if (err?.response?.status === 409) {
                const message: string = err?.response?.data?.message || ''
                if (/slug/i.test(message)) {
                    setError('slug', { message: 'This custom URL is already in use. Choose another one.' })
                } else {
                    // stale version: someone else saved first
                    setConflict(
                        message || 'This article was changed by someone else while you were editing.'
                    )
                }
                return
            }
            notification.push(getErrorResponse(err))
        } finally {
            setBusy(false)
        }
    }

    if (isLoading) return <LoaderMain size="md" text="Loading article..." fullscreen />

    if (isError || !article) {
        return (
            <div className="px-6 py-10 text-center">
                <p className="text-sm text-gray-600 dark:text-gray-300">
                    This article could not be loaded. It may have been deleted.
                </p>
                <button
                    type="button"
                    onClick={() => navigate('/articles')}
                    className="mt-4 rounded bg-gray-300 px-4 py-2 text-sm dark:bg-slate-700"
                >
                    Back to articles
                </button>
            </div>
        )
    }

    const onReview = async (decision: 'approved' | 'changes_requested', note: string) => {
        if (!article || !id) return
        setBusy(true)
        setConflict(null)
        try {
            await reviewArticle.mutateAsync({
                id,
                decision,
                note,
                expected_version: article.version,
            })
            queryClient.invalidateQueries([QueryKeys.ARTICLES])
            queryClient.invalidateQueries([QueryKeys.ARTICLE_DETAIL, id])
            notification.push({
                title: decision === 'approved' ? 'Article approved' : 'Changes requested',
                type: 'success',
                message: decision === 'approved'
                    ? 'The article can now be published.'
                    : 'The creator can edit the article and submit it again.',
            })
            navigate('/articles')
        } catch (err: any) {
            if (err?.response?.status === 409) {
                setConflict(err?.response?.data?.message || 'This article was changed by someone else.')
                return
            }
            notification.push(getErrorResponse(err))
        } finally {
            setBusy(false)
        }
    }

    return (
        <ArticleForm
            mode="edit"
            article={article}
            isSubmitting={busy}
            readOnly={readOnly || !canUpdate}
            canSubmit={canSubmitArticle}
            canPublish={canPublishArticle}
            canReview={canReviewArticle}
            onReview={onReview}
            onSubmit={onSubmit}
            banner={
                conflict && (
                    <div
                        role="alert"
                        className="mb-6 flex flex-col gap-3 rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-100 sm:flex-row sm:items-center sm:justify-between"
                    >
                        <p>
                            {conflict} Reload the latest version to continue. Reloading replaces
                            the edits in this form, so copy anything you want to keep first.
                        </p>
                        <button
                            type="button"
                            onClick={async () => {
                                await refetch()
                                setConflict(null)
                            }}
                            className="shrink-0 rounded bg-amber-600 px-4 py-2 text-white hover:bg-amber-700"
                        >
                            Reload latest
                        </button>
                    </div>
                )
            }
        />
    )
}

export default EditArticle